import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Page, PageHeader } from '../components/Page';
import { PCard } from '../components/PCard';
import { StatusPill } from '../components/StatusPill';
import { Async } from '../components/Async';
import { useFetch } from '../lib/useFetch';
import { useBackTo } from '../lib/backTo';
import { useAuth } from '../lib/auth';
import {
  getInvoice,
  invoicePdfUrl,
  invoiceSupportingCsvUrl,
  finalizeInvoice,
  updateInvoice,
  markInvoicePaid,
  createPayLink,
  getInvoiceEmailAttempts,
  sendInvoiceEmail,
  CARD_PAYMENT_MAX_CENTS,
  EMAILABLE_FLOWS,
  ApiError,
  type InvoiceEmailAttempt,
  type InvoiceEmailStatus,
  type SendInvoiceEmailMode,
  type InvoiceFull,
} from '../lib/api';
import { fmtCents } from '../lib/format';
import { invoiceStatusBadge } from '../lib/types';

const CHARGEABLE = new Set(['open', 'past_due', 'partial']);

const fmtDate = (s?: string) => (s ? new Date(s).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const PAY_TONE: Record<string, 'success' | 'error' | 'pending' | 'neutral'> = {
  succeeded: 'success',
  failed: 'error',
  refunded: 'neutral',
  pending: 'pending',
};

export const InvoiceDetailPage = () => {
  const { id } = useParams();
  const { can } = useAuth();
  const state = useFetch(() => getInvoice(id!), [id]);
  // Whichever list sent us here — a client's own invoices, Payments, the
  // dashboard — or the full list when we were opened cold.
  const back = useBackTo({ to: '/invoices', label: 'All invoices' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function finalize() {
    if (!window.confirm('Finalize this draft? It will be issued (invoice number + dates assigned) and enter the normal charge/pay flow.')) return;
    setBusy(true);
    setErr(null);
    try {
      await finalizeInvoice(id!);
      state.reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Finalize failed');
    } finally {
      setBusy(false);
    }
  }

  async function discard() {
    if (!window.confirm('Discard this draft? Its orders/shipments are released back to the next billing run.')) return;
    setBusy(true);
    setErr(null);
    try {
      await updateInvoice(id!, { status: 'void' });
      state.reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Discard failed');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Retire an already-issued invoice. Both outcomes make it uncollectible and
   * drop it out of AR; they differ in what happens to the usage it claimed.
   *   - void:   the invoice was wrong, so the fills are released and land on a
   *             corrected invoice.
   *   - cancel: the invoice should never have existed, so the fills stay
   *             claimed and are never billed again.
   * The server requires a stated reason for either.
   */
  async function retireIssued(kind: 'rebill' | 'cancel', invoiceNumber?: string) {
    const label = invoiceNumber ?? 'this invoice';
    const verb = kind === 'cancel' ? 'Cancel' : 'Void';
    const consequence =
      kind === 'cancel'
        ? 'its fills stay claimed, so they will never be billed again'
        : 'its fills are released back to be re-billed on the next run';
    const reason = window.prompt(
      `${verb} ${label}? It stops being collectible and drops out of AR, and ${consequence}.\n\nReason (required):`,
    );
    if (reason === null) return; // dismissed
    if (!reason.trim()) {
      setErr(`A reason is required to ${verb.toLowerCase()} an issued invoice.`);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await updateInvoice(id!, { status: 'void', voidKind: kind, voidReason: reason.trim() });
      state.reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : `${verb} failed`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page>
      <Async state={state} isEmpty={() => false} emptyLabel="">
        {({ invoice: inv, payments }) => {
          const s = invoiceStatusBadge(inv.status, inv.voidKind);
          const isDraft = inv.status === 'draft';
          const isVoid = inv.status === 'void';
          const isCancelled = isVoid && inv.voidKind === 'cancel';
          const canManage = can('invoices:manage');
          // Money on the invoice must be refunded through the rail first —
          // the server rejects the void otherwise.
          const canVoid = canManage && !isDraft && !isVoid && inv.amountPaidCents === 0;
          return (
            <>
              <PageHeader
                title={inv.invoiceNumber ?? (isDraft ? 'Draft invoice' : `Invoice ${inv.id.slice(-6)}`)}
                sub={<Link to={back.to} style={{ color: 'var(--p-muted-fg)', fontSize: 13 }}>← {back.label}</Link>}
                actions={
                  <>
                    <StatusPill tone={s.tone}>{s.label}</StatusPill>
                    <a className="btn" href={invoicePdfUrl(inv.id)} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>PDF</a>
                    {(inv.flow === 'pharmacy' || inv.flow === 'shipping') && (
                      <a className="btn" href={invoiceSupportingCsvUrl(inv.id)} style={{ textDecoration: 'none' }}>Supporting CSV</a>
                    )}
                    {isDraft && canManage && (
                      <>
                        <button className="btn" onClick={discard} disabled={busy}>Discard</button>
                        <button className="btn btn--primary" onClick={finalize} disabled={busy}>
                          {busy ? 'Working…' : 'Finalize & send'}
                        </button>
                      </>
                    )}
                    {canVoid && (
                      <>
                        <button
                          className="btn"
                          onClick={() => retireIssued('cancel', inv.invoiceNumber)}
                          disabled={busy}
                          title="This invoice should never have existed — keep its fills claimed so they are never billed again"
                        >
                          {busy ? 'Working…' : 'Cancel'}
                        </button>
                        <button
                          className="btn"
                          onClick={() => retireIssued('rebill', inv.invoiceNumber)}
                          disabled={busy}
                          title="This invoice was wrong — release its fills to be re-billed on a corrected invoice"
                        >
                          {busy ? 'Working…' : 'Void & re-bill'}
                        </button>
                      </>
                    )}
                  </>
                }
              />

              {isDraft && (
                <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(100,116,139,.1)', color: 'var(--p-muted-fg)', fontSize: 13 }}>
                  This is a <strong>draft</strong> for internal review — no invoice number, and it won’t be charged or shown to the client until finalized. Finalizing assigns the number and computes due/charge dates from today.
                </div>
              )}
              {isVoid && inv.voidedAt && (
                <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(100,116,139,.1)', color: 'var(--p-muted-fg)', fontSize: 13 }}>
                  <strong>{isCancelled ? 'Cancelled' : 'Voided'}</strong> {fmtDate(inv.voidedAt)}{inv.voidedBy ? ` by ${inv.voidedBy}` : ''}
                  {inv.voidReason ? ` — ${inv.voidReason}` : ''}. It is no longer collectible, is excluded from AR,
                  and its fills {isCancelled ? 'stay claimed, so they will never be billed again' : 'were released back to be re-billed'}.
                </div>
              )}
              {err && (
                <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(239,68,68,.08)', color: 'var(--status-error-ink,#b91c1c)', fontSize: 13 }}>
                  {err}
                </div>
              )}

              <PCard style={{ padding: 16, display: 'flex', flexWrap: 'wrap', gap: 24, fontSize: 13 }}>
                <Meta label="Client" value={<Link to={`/clients/${inv.clientId}`}>{inv.clientName}</Link>} />
                <Meta label="Type" value={inv.flow} />
                <Meta label="Period" value={inv.period.label} />
                <Meta label="Collection" value={inv.collectionMethod} />
                <Meta label="Issued" value={fmtDate(inv.issuedAt)} />
                <Meta label="Due" value={fmtDate(inv.dueAt)} />
                {inv.chargeAt && <Meta label="Auto-charge" value={fmtDate(inv.chargeAt)} />}
              </PCard>

              {!isDraft && <PaymentActions inv={inv} onDone={state.reload} />}

              {canManage
                && EMAILABLE_FLOWS.includes(inv.flow)
                && !isDraft
                && !isVoid
                && inv.issuedAt
                && <EmailDeliveryCard invoiceId={inv.id} />}

              <div>
                <div className="section-title">Line items</div>
                <PCard>
                  <table className="p-table">
                    <thead>
                      <tr><th>Description</th><th style={{ textAlign: 'right' }}>Qty</th><th style={{ textAlign: 'right' }}>Unit</th><th style={{ textAlign: 'right' }}>Amount</th></tr>
                    </thead>
                    <tbody>
                      {inv.lineItems.map((li, i) => (
                        <tr key={i}>
                          <td>{li.description}{li.kind === 'adjustment' && <span className="muted" style={{ fontSize: 11 }}> · adjustment</span>}</td>
                          <td className="tabular muted" style={{ textAlign: 'right' }}>{li.quantity}</td>
                          <td className="tabular muted" style={{ textAlign: 'right' }}>{fmtCents(li.unitPriceCents)}</td>
                          <td className="tabular" style={{ textAlign: 'right' }}>{fmtCents(li.amountCents)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <Total label="Subtotal" value={inv.subtotalCents} />
                      {inv.taxCents > 0 && <Total label="Tax" value={inv.taxCents} />}
                      <Total label="Total" value={inv.totalCents} bold />
                      {inv.amountPaidCents > 0 && <Total label="Paid" value={-inv.amountPaidCents} />}
                      <Total label="Amount due" value={inv.amountDueCents} bold />
                    </tfoot>
                  </table>
                </PCard>
              </div>

              <div>
                <div className="section-title">Payment attempts</div>
                {payments.length === 0 ? (
                  <PCard style={{ padding: 16, color: 'var(--p-muted-fg)', fontSize: 13 }}>No payment attempts yet.</PCard>
                ) : (
                  <PCard>
                    <table className="p-table">
                      <thead><tr><th>When</th><th>Provider</th><th>Status</th><th style={{ textAlign: 'right' }}>Amount</th><th>Detail</th></tr></thead>
                      <tbody>
                        {payments.map((p) => (
                          <tr key={p.id}>
                            <td className="muted">{fmtDate(p.attemptedAt)}</td>
                            <td className="muted">{p.provider}</td>
                            <td><StatusPill tone={PAY_TONE[p.status] ?? 'neutral'}>{p.status}</StatusPill></td>
                            <td className="tabular" style={{ textAlign: 'right' }}>{fmtCents(p.amountCents)}</td>
                            <td className="muted" style={{ fontSize: 12 }}>
                              {p.reference
                                ? `${(p.method ?? 'manual').toUpperCase()} · ${p.reference}`
                                : p.method
                                  ? p.method.toUpperCase()
                                  : (p.failureReason ?? p.providerPaymentId ?? '')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </PCard>
                )}
              </div>
            </>
          );
        }}
      </Async>
    </Page>
  );
};

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--p-border)',
  borderRadius: 6,
  padding: '6px 10px',
  fontFamily: 'inherit',
  fontSize: 13,
  background: '#fff',
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <span className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</span>
    {children}
  </label>
);

/** Collect payment on an issued invoice: record an offline (wire/ACH/check)
 *  payment with a confirmation number, or copy a hosted card pay link (only
 *  under the $20k card cap). Staff (payments:charge) only. */
function PaymentActions({ inv, onDone }: { inv: InvoiceFull; onDone: () => void }) {
  const { can } = useAuth();
  const chargeable = CHARGEABLE.has(inv.status) && inv.amountDueCents > 0;
  const cardOk = inv.totalCents < CARD_PAYMENT_MAX_CENTS;
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState((inv.amountDueCents / 100).toFixed(2));
  const [method, setMethod] = useState<'wire' | 'ach' | 'check' | 'other'>(inv.collectionMethod === 'ach' ? 'ach' : 'wire');
  const [reference, setReference] = useState('');
  const [paidAt, setPaidAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  if (!can('payments:charge') || !chargeable) return null;

  async function submit() {
    const d = parseFloat(amount);
    if (Number.isNaN(d) || d <= 0) { setErr('Enter a valid amount.'); return; }
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await markInvoicePaid(inv.id, {
        amountCents: Math.round(d * 100),
        method,
        reference: reference.trim() || undefined,
        paidAt: paidAt || undefined,
      });
      setMsg(`Payment recorded — invoice ${r.status}.`);
      setOpen(false);
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not record payment');
    } finally {
      setBusy(false);
    }
  }

  async function payLink() {
    setErr(null); setMsg(null);
    try {
      const { token } = await createPayLink(inv.id);
      const url = `${window.location.origin}/pay/${token}`;
      try {
        await navigator.clipboard.writeText(url);
        setMsg(`Pay link copied: ${url}`);
      } catch {
        setMsg(`Pay link: ${url}`);
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not create link');
    }
  }

  return (
    <div>
      <div className="section-title">Collect payment</div>
      <PCard style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--p-muted-fg)' }}>
            Amount due <strong style={{ color: 'var(--p-foreground)' }}>{fmtCents(inv.amountDueCents)}</strong>
          </span>
          <div style={{ flex: 1 }} />
          <button className="btn btn--primary" onClick={() => setOpen((o) => !o)}>
            {open ? 'Cancel' : 'Mark as paid'}
          </button>
          {cardOk ? (
            <button className="btn" onClick={payLink}>Copy pay link</button>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--status-attention-ink,#92400e)', background: 'rgba(251,191,36,.12)', padding: '4px 10px', borderRadius: 999 }}>
              ≥ {fmtCents(CARD_PAYMENT_MAX_CENTS)} — wire/ACH only (card & pay link disabled)
            </span>
          )}
        </div>

        {open && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', borderTop: '1px solid var(--p-border)', paddingTop: 12 }}>
            <Field label="Amount ($)"><input value={amount} onChange={(e) => setAmount(e.target.value)} style={{ ...inputStyle, width: 110 }} /></Field>
            <Field label="Method">
              <select className="select" value={method} onChange={(e) => setMethod(e.target.value as 'wire' | 'ach' | 'check' | 'other')}>
                <option value="wire">Wire</option>
                <option value="ach">ACH</option>
                <option value="check">Check</option>
                <option value="other">Other</option>
              </select>
            </Field>
            <Field label="Confirmation # (proof)">
              <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="wire confirmation #" style={{ ...inputStyle, minWidth: 220 }} />
            </Field>
            <Field label="Received"><input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} style={inputStyle} /></Field>
            <button className="btn btn--primary" onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Record payment'}</button>
          </div>
        )}

        {msg && <div style={{ fontSize: 13, color: 'var(--status-success-ink,#047857)', wordBreak: 'break-all' }}>{msg}</div>}
        {err && <div style={{ fontSize: 13, color: 'var(--status-error-ink,#b91c1c)' }}>{err}</div>}
      </PCard>
    </div>
  );
}

const Meta = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div><div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div><div>{value}</div></div>
);
const Total = ({ label, value, bold }: { label: string; value: number; bold?: boolean }) => (
  <tr style={bold ? { borderTop: '2px solid var(--p-border)' } : undefined}>
    <td colSpan={3} style={{ textAlign: 'right', fontWeight: bold ? 700 : 400, color: bold ? undefined : 'var(--p-muted-fg)' }}>{label}</td>
    <td className="tabular" style={{ textAlign: 'right', fontWeight: bold ? 700 : 400 }}>{fmtCents(value)}</td>
  </tr>
);

const EMAIL_TONE: Record<InvoiceEmailStatus, 'success' | 'error' | 'pending' | 'neutral'> = {
  pending: 'pending',
  submitting: 'pending',
  accepted: 'pending',
  delivered: 'success',
  bounced: 'error',
  failed: 'error',
  uncertain: 'neutral',
};

const emailLabel = (value: string) => value.replaceAll('_', ' ');

const eventLabel = (value: string) => {
  if (value === 'clicked' || value === 'click') return 'Link requested';
  return emailLabel(value);
};

function latestAttemptByRecipient(attempts: InvoiceEmailAttempt[]) {
  const latest = new Map<string, InvoiceEmailAttempt>();
  for (const attempt of attempts) {
    const recipientKey = attempt.recipient.trim().toLowerCase();
    const prior = latest.get(recipientKey);
    if (!prior || Date.parse(attempt.attemptedAt) > Date.parse(prior.attemptedAt)) {
      latest.set(recipientKey, attempt);
    }
  }
  return [...latest.values()];
}

/** Staff-only audit and controlled resend surface. The parent permission gate is
 * intentionally outside this component so clinic users never issue the audit request. */
export function EmailDeliveryCard({ invoiceId }: { invoiceId: string }) {
  const audit = useFetch(() => getInvoiceEmailAttempts(invoiceId), [invoiceId]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const attempts = audit.data?.attempts ?? [];
  const legacyEmailedAt = audit.data?.legacyEmailedAt;
  const neverSent = attempts.length === 0 && !legacyEmailedAt;
  const hasDefiniteFailure = latestAttemptByRecipient(attempts).some((attempt) => attempt.status === 'failed');
  const action: { label: string; mode: SendInvoiceEmailMode } = neverSent
    ? { label: 'Send invoice', mode: 'initial' }
    : hasDefiniteFailure
      ? { label: 'Retry failed', mode: 'retry_unsent' }
      : { label: 'Resend invoice', mode: 'resend_all' };

  async function send() {
    if (
      action.mode === 'resend_all'
      && !window.confirm('Resend this invoice to every recipient? This creates a new email attempt for each recipient.')
    ) return;

    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const result = await sendInvoiceEmail(invoiceId, action.mode);
      setMessage(
        result.sent > 0
          ? `Postmark accepted ${result.sent} new email${result.sent === 1 ? '' : 's'}; delivery updates will appear below.`
          : result.results.length === 0
            ? 'No recipients needed a new email.'
            : 'No new email was accepted by this request; review the refreshed statuses below.',
      );
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not send invoice email');
    } finally {
      audit.reload();
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span>Email delivery</span>
        <span style={{ flex: 1 }} />
        <button
          className={action.mode === 'resend_all' ? 'btn' : 'btn btn--primary'}
          onClick={send}
          disabled={busy || audit.loading || !audit.data}
        >
          {busy ? 'Sending…' : action.label}
        </button>
      </div>
      <PCard style={{ padding: 16 }}>
        <div aria-live="polite">
          {message && <div style={{ color: 'var(--status-success-ink,#047857)', fontSize: 13, marginBottom: 12 }}>{message}</div>}
          {error && <div style={{ color: 'var(--status-error-ink,#b91c1c)', fontSize: 13, marginBottom: 12 }}>{error}</div>}
        </div>

        {audit.loading ? (
          <div className="muted" style={{ fontSize: 13 }}>Loading email history…</div>
        ) : audit.error ? (
          <div style={{ color: 'var(--status-error-ink,#b91c1c)', fontSize: 13 }}>Couldn’t load email history — {audit.error}</div>
        ) : attempts.length === 0 ? (
          <div className="muted" style={{ fontSize: 13 }}>
            {legacyEmailedAt
              ? `Legacy send recorded ${fmtDate(legacyEmailedAt)}; delivery status is unknown.`
              : 'This invoice has not been emailed yet.'}
          </div>
        ) : (
          <div role="list" aria-label="Invoice email attempts" style={{ display: 'flex', flexDirection: 'column' }}>
            {[...attempts]
              .sort((a, b) => Date.parse(b.attemptedAt) - Date.parse(a.attemptedAt))
              .map((attempt, index) => (
                <EmailAttemptRow key={attempt.id} attempt={attempt} separated={index > 0} />
              ))}
          </div>
        )}
      </PCard>
    </div>
  );
}

function EmailAttemptRow({ attempt, separated }: { attempt: InvoiceEmailAttempt; separated: boolean }) {
  const events = [...attempt.events].sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt));
  const lastClickIsDistinct = attempt.lastClickedAt && attempt.lastClickedAt !== attempt.firstClickedAt;

  return (
    <div
      role="listitem"
      style={{
        paddingTop: separated ? 14 : 0,
        marginTop: separated ? 14 : 0,
        borderTop: separated ? '1px solid var(--p-border)' : undefined,
      }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13 }}>{attempt.recipient}</strong>
        <StatusPill tone={EMAIL_TONE[attempt.status]}>{emailLabel(attempt.status)}</StatusPill>
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        Attempted {fmtDate(attempt.attemptedAt)} · {emailLabel(attempt.trigger)}
        {attempt.requestedBy ? ` by ${attempt.requestedBy}` : ' automatically'}
      </div>

      {(attempt.submittedAt || attempt.deliveredAt || attempt.bouncedAt || attempt.firstClickedAt) && (
        <div className="muted" style={{ display: 'flex', gap: '4px 14px', flexWrap: 'wrap', fontSize: 12, marginTop: 6 }}>
          {attempt.submittedAt && <span>Submitted {fmtDate(attempt.submittedAt)}</span>}
          {attempt.deliveredAt && <span>Delivered {fmtDate(attempt.deliveredAt)}</span>}
          {attempt.bouncedAt && <span>Bounced {fmtDate(attempt.bouncedAt)}</span>}
          {attempt.firstClickedAt && <span>First link request {fmtDate(attempt.firstClickedAt)}</span>}
          {lastClickIsDistinct && <span>Latest link request {fmtDate(attempt.lastClickedAt)}</span>}
        </div>
      )}

      {attempt.failureReason && (
        <div style={{ color: 'var(--status-error-ink,#b91c1c)', fontSize: 12, marginTop: 6 }}>{attempt.failureReason}</div>
      )}

      {events.length > 0 && (
        <ol aria-label={`Events for ${attempt.recipient}`} style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12 }}>
          {events.map((event, index) => (
            <li key={`${event.type}-${event.occurredAt}-${index}`} style={{ marginTop: 3 }}>
              <span style={{ textTransform: 'capitalize' }}>{eventLabel(event.type)}</span>
              <span className="muted"> · {fmtDate(event.occurredAt)}</span>
              {event.detail && <span className="muted"> · {event.detail}</span>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
